/**
 * Kilometerregistratie Service — privéauto zakelijk gebruik
 */

import apiFetch from './api';

export interface PriveAuto {
  id: number;
  naam: string;
  kenteken: string | null;
  actief: boolean;
}

export interface Rit {
  id: number;
  auto_id: number;
  auto_naam: string | null;
  datum: string;
  bestemming: string;
  omschrijving: string | null;
  km_heen: number;
  km_terug: number;
  km_totaal: number;
  tarief: number;
  vergoeding: number;
  created_at: string | null;
}

export interface RitCreate {
  auto_id: number;
  datum: string;
  bestemming: string;
  omschrijving?: string;
  km_heen: number;
  km_terug: number;
  tarief: number;
}

export interface RitUpdate {
  auto_id?: number;
  datum?: string;
  bestemming?: string;
  omschrijving?: string;
  km_heen?: number;
  km_terug?: number;
  tarief?: number;
}

export interface RitListResponse {
  items: Rit[];
  total: number;
}

export interface AutoOverzicht {
  auto_id: number;
  auto_naam: string;
  km_totaal: number;
  vergoeding_totaal: number;
  aantal_ritten: number;
}

export interface JaarOverzicht {
  jaar: number;
  autos: AutoOverzicht[];
  totaal_km: number;
  totaal_vergoeding: number;
}

export interface RelatieOptie {
  id: number;
  naam: string;
  plaats: string | null;
  type: string;
}

const BASE = '/api/v1/kilometerregistratie';

export const kilometerregistratieService = {
  // Auto's
  async getAutos(): Promise<PriveAuto[]> {
    return apiFetch(`${BASE}/autos`);
  },

  async createAuto(data: { naam: string; kenteken?: string }): Promise<PriveAuto> {
    return apiFetch(`${BASE}/autos`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAuto(id: number, data: { naam?: string; kenteken?: string }): Promise<PriveAuto> {
    return apiFetch(`${BASE}/autos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAuto(id: number): Promise<void> {
    return apiFetch(`${BASE}/autos/${id}`, { method: 'DELETE' });
  },

  // Relaties
  async getRelaties(): Promise<RelatieOptie[]> {
    return apiFetch(`${BASE}/relaties`);
  },

  async getAfstand(relatieId: number): Promise<{ km: number; bron: string }> {
    return apiFetch(`${BASE}/afstand/${relatieId}`);
  },

  // Ritten
  async getRitten(params?: {
    auto_id?: number;
    jaar?: number;
    skip?: number;
    limit?: number;
  }): Promise<RitListResponse> {
    const query = new URLSearchParams();
    if (params?.auto_id) query.set('auto_id', params.auto_id.toString());
    if (params?.jaar) query.set('jaar', params.jaar.toString());
    if (params?.skip) query.set('skip', params.skip.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    const qs = query.toString();
    return apiFetch(`${BASE}/${qs ? '?' + qs : ''}`);
  },

  async createRit(data: RitCreate): Promise<Rit> {
    return apiFetch(`${BASE}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRit(id: number, data: RitUpdate): Promise<Rit> {
    return apiFetch(`${BASE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteRit(id: number): Promise<void> {
    return apiFetch(`${BASE}/${id}`, { method: 'DELETE' });
  },

  async getOverzicht(jaar: number): Promise<JaarOverzicht> {
    return apiFetch(`${BASE}/overzicht?jaar=${jaar}`);
  },
};
